import React, { useState } from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  Avatar, 
  Link, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Container,
  Divider
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import ForgotPassword from './ForgotPassword';

import { useNavigate } from "react-router-dom";

import { login } from '../../services/userService';
import { useUser } from '../../UserContext';
import { useAuth } from '../../AuthContext';
import { Utils } from '../../utils/utils';
import { APP_NAME } from '../../constants';
import soupaiBackground from '../../imgs/soupai.png';

import { jwtDecode } from "jwt-decode";
import theme from '../../theme';
import LoginWelcomeVerbiage from './LoginWelcomeVerbiage';
import { useLocale } from '../../i18n/LanguageContext';

const Login = (props) => {
  const { user, setUser } = useUser();
  const { auth, setAuth } = useAuth();
  const { t } = useLocale();
  const [userCredentials, setUserCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState(null);
  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [emailValidationError, setEmailValidationError] = useState('');
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserCredentials((prevUser) => ({
      ...prevUser,
      [name]: value,
    }));

    if (name === 'email') {
      if (value && !Utils.validateEmailFormat(value)) {
        setEmailValidationError(t('login.form.validEmail'));
      } else {
        setEmailValidationError('');
      }
    }
  };

  async function submitLogin()  {
    if (userCredentials.email && !Utils.validateEmailFormat(userCredentials.email)) {
      setEmailValidationError(t('login.form.validEmail'));
      return;
    }

    try {
        const loginResponse = await login(userCredentials);
        if (loginResponse?.token) {
          setAuth(loginResponse.token);
          const decodedToken = jwtDecode(loginResponse.token);
          const userLoggedIn = decodedToken?.userDTO;
          setUser(userLoggedIn);
          if (Utils.isCustomer(userLoggedIn)) {
            navigate("/list");
          } else if (Utils.isChef(userLoggedIn)) {
            navigate("/dashboard");
          } else {
            navigate("/");
          }
          
        } else {
          setUser(null);
          setAuth(null);
          setInvalidCredentials(true);
        }
    } catch (error) {
        setError(error.message);
    }
  }

  const handleOpenForgotPassword = () => {
    setForgotPasswordOpen(true);
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
  };

  if (error) {
    return <div>{t('login.error.generic')} {error}</div>;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
          {t('login.hero.welcome')} <span>
            <lable style={{ color: theme.palette.primary.main }}>Mia</lable><lable style={{ color: theme.palette.secondary.main }}>Panza</lable>
          </span>
        </Typography>
        <Typography variant="h6" style={{ color: theme.palette.primary.main }} sx={{ mb: 3 }}>
          {t('login.hero.location')}
        </Typography>

        <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
          {t('login.hero.headline')}
        </Typography>    
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
          {t('login.hero.description')}
        </Typography>
      </Box>

      {/* Mobile/Desktop ordered content */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {/* Hero Image */}
        <Grid item xs={12} md={7} sx={{ order: { xs: 1, md: 1 } }}>
          <Box 
            sx={{ 
              height: '100%',
              bgcolor: '#f5f5f5', 
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundImage: `url(${soupaiBackground})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              color: 'white'
            }}
          >
            <Typography variant="h4" sx={{ textAlign: 'center', p: 2, textShadow: '0 2px 8px rgba(0, 0, 0, 0.7)' }}>
              {t('login.hero.houseOfHomeCooking')} <br /> {t('login.hero.homeCooking')}
            </Typography>

            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 300,
                mt: 2
              }}
            >
              {APP_NAME}
            </Typography>
          </Box>
          
        </Grid>

        <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' }, order: 2 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
            {t('login.howItWorks')}
          </Typography>

          <Grid container spacing={4}>
            {/* For Customers */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', boxShadow: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ShoppingCartIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {t('login.customer.title')}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <LocationOnIcon sx={{ color: theme.palette.primary.main, mr: 1, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {t('login.customer.steps.one.title')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('login.customer.steps.one.description')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <RestaurantIcon sx={{ color: theme.palette.primary.main, mr: 1, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {t('login.customer.steps.two.title')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('login.customer.steps.two.description')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <StarIcon sx={{ color: theme.palette.primary.main, mr: 1, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {t('login.customer.steps.three.title')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('login.customer.steps.three.description')}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* For Chefs */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', boxShadow: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <RestaurantIcon sx={{ fontSize: 40, color: theme.palette.secondary.main, mr: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {t('login.chef.title')}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Typography sx={{ color: theme.palette.secondary.main, mr: 1, mt: 0.5, fontWeight: 'bold' }}>
                      1.
                    </Typography>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {t('login.chef.steps.one.title')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('login.chef.steps.one.description')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Typography sx={{ color: theme.palette.secondary.main, mr: 1, mt: 0.5, fontWeight: 'bold' }}>
                      2.
                    </Typography>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {t('login.chef.steps.two.title')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('login.chef.steps.two.description')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Typography sx={{ color: theme.palette.secondary.main, mr: 1, mt: 0.5, fontWeight: 'bold' }}>
                      3.
                    </Typography>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {t('login.chef.steps.three.title')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('login.chef.steps.three.description')}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' }, order: 3 }}>
          <LoginWelcomeVerbiage embedded />
        </Grid>

        {/* Login Box */}
        <Grid item xs={12} md={5} sx={{ order: { xs: 4, md: 2 } }}>
          <Card sx={{ height: '100%', boxShadow: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ mb: 3 }}>
                {t('login.form.signIn')}
              </Typography>
              
              <form>
                { invalidCredentials ? (
                  <Typography color="error" sx={{ mb: 2 }}>
                    {t('login.form.invalidCredentials')}
                  </Typography>
                ) : null}

                <TextField
                  label={t('login.form.email')}
                  name="email"
                  value={userCredentials.email}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  error={!!emailValidationError}
                  helperText={emailValidationError}
                />

                <TextField
                  label={t('login.form.password')}
                  name="password"
                  value={userCredentials.password}
                  type='password'
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                />

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3, alignItems: 'center' }}>
                  <Link style={{ cursor: 'pointer', textDecoration: 'none' }} onClick={handleOpenForgotPassword}>{t('login.form.forgotPassword')}</Link>
                  <Button 
                    style={{ textTransform: 'none' }} 
                    onClick={submitLogin} 
                    variant="contained" 
                    color="primary"
                  >
                    {t('login.form.login')}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Features Section */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
      <Typography variant="h4" align="center" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
        {t('login.howItWorks')}
      </Typography>

      <Grid container spacing={4} sx={{ mb: 6 }}>
        {/* For Customers */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', boxShadow: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShoppingCartIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {t('login.customer.title')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <LocationOnIcon sx={{ color: theme.palette.primary.main, mr: 1, mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {t('login.customer.steps.one.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('login.customer.steps.one.description')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <RestaurantIcon sx={{ color: theme.palette.primary.main, mr: 1, mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {t('login.customer.steps.two.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('login.customer.steps.two.description')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <StarIcon sx={{ color: theme.palette.primary.main, mr: 1, mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {t('login.customer.steps.three.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('login.customer.steps.three.description')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* For Chefs */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', boxShadow: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RestaurantIcon sx={{ fontSize: 40, color: theme.palette.secondary.main, mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {t('login.chef.title')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Typography sx={{ color: theme.palette.secondary.main, mr: 1, mt: 0.5, fontWeight: 'bold' }}>
                  1.
                </Typography>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {t('login.chef.steps.one.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('login.chef.steps.one.description')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Typography sx={{ color: theme.palette.secondary.main, mr: 1, mt: 0.5, fontWeight: 'bold' }}>
                  2.
                </Typography>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {t('login.chef.steps.two.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('login.chef.steps.two.description')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Typography sx={{ color: theme.palette.secondary.main, mr: 1, mt: 0.5, fontWeight: 'bold' }}>
                  3.
                </Typography>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {t('login.chef.steps.three.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('login.chef.steps.three.description')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>

      {/* Second Image Placeholder */}
      <Box 
        sx={{ 
          height: 140, 
          bgcolor: '#f5f5f5', 
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white'
        }}
      >
        <Typography variant="h5" sx={{ textAlign: 'center', px: 2 }}>
          {t('login.quote.text')} <br /> {t('login.quote.author')}
        </Typography>
      </Box>

      <ForgotPassword
        open={forgotPasswordOpen}
        email={userCredentials.email}
        onClose={handleCloseForgotPassword}
      />
    </Container>
  );
};

export default Login;
